import { NgClass } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterOutlet,
} from "@angular/router";
import { FuseFullscreenComponent } from "@fuse/components/fullscreen";
import { FuseLoadingBarComponent } from "@fuse/components/loading-bar";
import {
  FuseNavigationService,
  FuseNavigationItem,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { AnimationOptions, LottieComponent } from "ngx-lottie";
import { Subject, filter, take, takeUntil } from "rxjs";

import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { AppConstants } from "app/core/constants";
import { AuthService } from "app/core/auth/auth.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { BreadcrumbService } from "app/core/services/breadcrumb.service";
import { CookieService } from "app/core/services/cookie.service";
import { SystemService } from "app/core/systems/system.service";
import { environment } from "app/environments/environment";
import { LanguagesComponent } from "app/layout/common/languages/languages.component";
import { UserComponent } from "app/layout/common/user/user.component";
import { BreadcrumbModule } from "primeng/breadcrumb";
@Component({
  selector: "dense-layout",
  templateUrl: "./dense.component.html",
  styleUrls: ["./dense.component.scss"],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    LanguagesComponent,
    FuseFullscreenComponent,
    // SearchComponent,
    // ShortcutsComponent,
    // MessagesComponent,
    // NotificationsComponent,
    UserComponent,
    RouterOutlet,
    LottieComponent,
    //QuickChatComponent,
    RouterLink,
    NgClass,
    LanguagesComponent,
    TranslocoModule,
    BreadcrumbModule,
    // JsonPipe,
  ],
})
export class DenseLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly translocoService = inject(TranslocoService);
  private readonly _matDialog = inject(MatDialog);
  isScreenSmall = false;
  navOpened = false;
  private readonly NAV_COOKIE_KEY = "nav_opened_mainNavigation";
  navigation: Navigation = { default: [] };
  navigationAppearance: "default" | "dense" = "dense";
  private readonly _unsubscribeAll: Subject<any> = new Subject<any>();
  private readonly _handlePointerMoveOutsideNavigation = (
    event: PointerEvent,
  ): void => {
    if (!this.isScreenSmall || !this.navOpened) {
      return;
    }

    const navEl = document.querySelector("fuse-vertical-navigation");
    if (!navEl) {
      return;
    }

    const rect = navEl.getBoundingClientRect();
    const insideNavigation =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;

    if (insideNavigation) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (
      target?.closest(".topnav-icon-button, .topnav-bar, .topnav-right-group")
    ) {
      return;
    }

    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        "mainNavigation",
      );

    navigation?.close();
    this.navOpened = false;
  };
  style: Partial<CSSStyleDeclaration> = {
    margin: "0 auto",
    height: "60px",
    width: "126px",
  };
  options: AnimationOptions = {
    path: "/assets/jsons/tesvolt_portal_logo_start.json",
    loop: false,
    autoplay: true,
  };
  version: string;
  isTopNavVisible = true;
  isTopNavHovered = false;
  isBatteryPassRoute = false;
  isAnonymousBatteryPass = false;
  readonly partnerPortalUrl = environment.partnerPortalUrl;
  // Spec §5: hide when scrollY >= 80 and scrolling down; reveal on up
  private readonly topNavScrollThreshold = 80;
  // Small jitter guard to ignore sub-pixel wheel noise
  private readonly topNavJitterPx = 3;
  // Spec §5: 2000ms delay before hiding after mouse leaves
  private readonly topNavHideDelayMs = 2000;
  private lastScrollY = 0;
  private hideTopNavTimer: ReturnType<typeof setTimeout> | null = null;
  private onWindowScroll?: () => void;
  private isDialogOpen = false;
  private breadcrumbTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Constructor
   */
  constructor(
    private readonly _fuseMediaWatcherService: FuseMediaWatcherService,
    private readonly _fuseNavigationService: FuseNavigationService,
    private readonly _ngZone: NgZone,
    public readonly breadcrumbService: BreadcrumbService,
    private readonly _systemService: SystemService,
    private readonly _navigationContentEl: ElementRef,
    private readonly _breadCrumbService: BreadcrumbService,
    private readonly _translocoService: TranslocoService,
    private readonly _cookieService: CookieService,
    private readonly _router: Router,
    private readonly _authService: AuthService,
  ) {
    this.version = environment.version;
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  ngAfterViewInit() {
    this.scheduleBreadcrumbUpdate(300);
    this.translocoService.langChanges$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.scheduleBreadcrumbUpdate(150);
      });
  }

  private scheduleBreadcrumbUpdate(delay = 0): void {
    if (this.breadcrumbTimer) {
      clearTimeout(this.breadcrumbTimer);
    }

    this.breadcrumbTimer = setTimeout(() => {
      this.setBreadcrumb();
      this.breadcrumbTimer = null;
    }, delay);
  }

  setBreadcrumb() {
    const currentUrl = this._router.url;
    const findItemTrail = (
      items: FuseNavigationItem[] | undefined,
      targetUrl: string,
      trail: FuseNavigationItem[] = [],
    ): FuseNavigationItem[] | null => {
      for (const item of items ?? []) {
        const nextTrail = item.title ? [...trail, item] : trail;

        if (item.link && targetUrl.includes(item.link)) {
          return nextTrail;
        }

        const childTrail = findItemTrail(
          item.children as FuseNavigationItem[] | undefined,
          targetUrl,
          nextTrail,
        );
        if (childTrail) {
          return childTrail;
        }
      }

      return null;
    };

    let itemTrail = findItemTrail(this.navigation.default, currentUrl);

    if (!itemTrail) {
      const ele = this._navigationContentEl?.nativeElement?.querySelector(
        ".fuse-vertical-navigation-item-active",
      );
      if (ele?.href) {
        itemTrail = findItemTrail(this.navigation.default, ele.href);
      }
    }

    if (!itemTrail?.length) {
      return;
    }

    const menuItems = [
      {
        label: "Home",
        routerLink: this.isBatteryPassRoute ? "/batterypass" : "/",
      },
    ];

    itemTrail.forEach((item, index) => {
      const translated = this._translocoService.translate(item.title ?? "");
      const label =
        item.title && translated !== item.title ? translated : item.title;

      if (!label) {
        return;
      }

      menuItems.push({
        label,
        routerLink: index === itemTrail.length - 1 ? "" : (item.link ?? ""),
      });
    });

    this._breadCrumbService.menuItems = menuItems;
  }

  /**
   * On init
   */

  ngOnInit(): void {
    this.lastScrollY = typeof window !== "undefined" ? window.scrollY : 0;
    this.setupTopNavScrollBehavior();
    this.isBatteryPassRoute = this._router.url
      .split("?")[0]
      .toLowerCase()
      .startsWith("/batterypass");
    this.isAnonymousBatteryPass =
      this.isBatteryPassRoute && !this._authService.getCurrentUser();

    if (this.isAnonymousBatteryPass) {
      this.navigation = { default: [this.getBatteryPassNavigationItem()] };
    }

    // Read the saved visual mode ONCE at startup.
    // false = dense/icon rail, true = expanded/default
    const savedState = this._cookieService.getCookie(this.NAV_COOKIE_KEY);
    this.navigationAppearance = savedState === "true" ? "default" : "dense";

    // Persist the current visual mode so the cookie always exists after first load
    this._cookieService.setBooleanCookie(
      this.NAV_COOKIE_KEY,
      this.navigationAppearance === "default",
    );

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes("md");

        // Change the navigation appearance
        if (this.isScreenSmall) {
          this.navOpened = false;
        }
      });
    this._systemService.systemList$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (data) => {
          if (this.isAnonymousBatteryPass) {
            return;
          }

          const hasPlants = data.length > 0;
          const operationChildren = [
            ...(this.isServiceTicketAllowed()
              ? [
                  {
                    id: "service-ticket",
                    title: "nav.operation.servicetickets",
                    type: "basic" as const,
                    link: "/operation/service-ticket",
                  },
                ]
              : []),

            ...(this.isNotificationProtocolAllowed()
              ? [
                  {
                    id: "notification-protocol",
                    title: "nav.focus.notification-protocol",
                    type: "basic" as const,
                    link: "/operation/notification-protocol",
                  },
                ]
              : []),
            ...(this.isAlertsPageAllowed()
              ? [
                  {
                    id: "idle-warning",
                    title: "nav.operation.idlewarning",
                    type: "basic" as const,
                    link: "/operation/idle-alerts",
                  },
                  {
                    id: "alarm-management",
                    title: "nav.operation.alarmmanagement",
                    type: "basic" as const,
                    link: "/operation/personalized-alarms",
                  },
                ]
              : []),
          ];

          const navigationItems: FuseNavigationItem[] = [
            ...(hasPlants
              ? [
                  {
                    id: "monitoring",
                    title: "nav.monitoring.monitoring",
                    type: "collapsable" as const,
                    icon: "custom_icon:NavMonitoring",
                    children: [
                      {
                        id: "plant-status",
                        title: "nav.monitoring.plantstatus",
                        type: "basic" as const,
                        link: "/monitoring/plant-status",
                      },
                      {
                        id: "performance-monitor",
                        title: "nav.monitoring.performancemonitor",
                        type: "basic" as const,
                        link: "/monitoring/performance-monitor",
                      },
                    ],
                  },
                ]
              : []),
            {
              id: "operations",
              title: "nav.monitoring.operation",
              type: "collapsable",
              icon: "custom_icon:NavOperation",
              children: operationChildren,
            },
            {
              id: "systems-devices",
              title: "nav.monitoring.systemsdevices",
              type: "collapsable" as const,
              icon: "custom_icon:NavDevices",
              children: [
                ...(this.isPlantManagementAllowed()
                  ? [
                      {
                        id: "plant-management",
                        title: "nav.setting.mysystem.myasset",
                        type: "basic" as const,
                        link: "/system-management/sys-home",
                        active: this.isPlantManagementWorkflow(
                          this._router.url,
                        ),
                      },
                    ]
                  : []),
              ],
            },
            {
              id: "user-management",
              title: "nav.setting.usermanagement.usermanagement",
              type: "collapsable",
              icon: "custom_icon:NavUsers",
              children: [
                {
                  id: "my-profile",
                  title: "nav.setting.myaccount.myprofile",
                  type: "basic",
                  link: "/accountsettings/myprofile",
                },
              ],
            },
            {
              id: "portal-settings",
              title: "nav.setting.portalsettings.portalsettings",
              type: "collapsable",
              icon: "custom_icon:NavSliders",
              children: [
                {
                  id: "version-history",
                  title: "nav.setting.versionhistory.versionhistoryview",
                  type: "basic",
                  link: "/portal-settings/version-history-view",
                },
                {
                  id: "notification-email",
                  title:
                    "nav.setting.notificationemail.notificationemailglobal",
                  type: "basic",
                  link: "/portal-settings/notification-email",
                },
              ],
            },
            ...(this.isBatteryPassAdminAllowed()
              ? [this.getBatteryPassNavigationItem()]
              : []),
          ];

          this.navigation = {
            default: this.pruneEmptyCollapsableGroups(navigationItems),
          };
          this.scheduleBreadcrumbUpdate(100);
        },
      });

    this._router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((event) => {
        const plantManagementItem = this._fuseNavigationService.getItem(
          "plant-management",
          this.navigation.default,
        );

        if (!plantManagementItem) {
          return;
        }

        plantManagementItem.active = this.isPlantManagementWorkflow(
          event.urlAfterRedirects,
        );
        this._fuseNavigationService
          .getComponent<FuseVerticalNavigationComponent>("mainNavigation")
          ?.refresh();
        this.scheduleBreadcrumbUpdate(100);
      });

    this._matDialog.afterOpened
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.isDialogOpen = true;
        this.clearTopNavHideTimer();
      });

    this._matDialog.afterAllClosed
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => {
        this.isDialogOpen = false;
      });

    document.addEventListener(
      "pointermove",
      this._handlePointerMoveOutsideNavigation,
    );
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    document.removeEventListener(
      "pointermove",
      this._handlePointerMoveOutsideNavigation,
    );
    this.teardownTopNavScrollBehavior();
    this.clearTopNavHideTimer();

    if (this.breadcrumbTimer) {
      clearTimeout(this.breadcrumbTimer);
      this.breadcrumbTimer = null;
    }

    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onTopNavMouseEnter(): void {
    if (this.isDialogOpen) {
      return;
    }

    this.isTopNavHovered = true;
    this.clearTopNavHideTimer();
    this.setTopNavVisible(true);
  }

  onTopNavMouseLeave(): void {
    if (this.isDialogOpen) {
      return;
    }

    this.isTopNavHovered = false;
    this.scheduleTopNavHide();
  }

  onTopHoverZoneEnter(): void {
    if (this.isDialogOpen) {
      return;
    }

    if (!this.isTopNavVisible) {
      this.setTopNavVisible(true);
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name,
      );

    if (navigation) {
      navigation.toggle();
      this.navOpened = navigation.opened;
    }
  }

  /**
   * Toggle the navigation appearance
   */
  toggleNavigationAppearance(): void {
    this._fuseNavigationService.navigationToggled.next();
    this.navigationAppearance =
      this.navigationAppearance === "default" ? "dense" : "default";
    this._cookieService.setBooleanCookie(
      this.NAV_COOKIE_KEY,
      this.navigationAppearance === "default",
    );
  }

  getPaddingClass() {
    const contentAreaWidth =
      document.getElementById("content-area")?.offsetWidth ?? 0;
    if (contentAreaWidth > 1330) {
      return "px-20";
    } else {
      return "px-10";
    }
  }

  getLocalizedLink(template: string): string {
    let lang = this.translocoService.getActiveLang();
    // fallback to 'en' if unsupported
    const supportedLangs = ["de", "en"];
    switch (lang) {
      case AppConstants.GB_ISO:
        lang = AppConstants.GB_LOCALE;
        break;
      case AppConstants.DE_ISO:
        lang = AppConstants.DE_ISO;
        break;
      default:
        lang = AppConstants.DE_ISO;
        break;
    }
    const activeLang = supportedLangs.includes(lang) ? lang : "de";

    return template.replace("{lang}", activeLang);
  }

  private isNotificationProtocolAllowed(): boolean {
    const portalRole = this.getCurrentUserPortalRole();
    if (!portalRole) {
      return false;
    }

    const normalizedRole = portalRole.trim().toLowerCase();
    return (
      normalizedRole === "tesvolt admin" || normalizedRole === "tesvolt service"
    );
  }

  private isServiceTicketAllowed(): boolean {
    const portalRole = this.getCurrentUserPortalRole();
    if (!portalRole) {
      return false;
    }

    const normalizedRole = portalRole.trim().toLowerCase();
    return (
      normalizedRole === "tesvolt admin" ||
      normalizedRole === "tesvolt service" ||
      normalizedRole === "installer admin" ||
      normalizedRole === "installer member"
    );
  }

  private isPlantManagementAllowed(): boolean {
    const portalRole = this.getCurrentUserPortalRole();
    if (!portalRole) {
      return false;
    }

    const normalizedRole = portalRole.trim().toLowerCase();
    return normalizedRole !== "tesvolt member";
  }

  private isPlantManagementWorkflow(url: string): boolean {
    return (
      url.startsWith("/system-management/sys-step") ||
      url.startsWith("/system-management/sys-plant-")
    );
  }

  private isAlertsPageAllowed(): boolean {
    const portalRole = this.getCurrentUserPortalRole();
    if (!portalRole) {
      return false;
    }

    const normalizedRole = portalRole.trim().toLowerCase();
    return normalizedRole === "tesvolt admin";
  }

  private isBatteryPassAdminAllowed(): boolean {
    return (
      this.getCurrentUserPortalRole()?.trim().toLowerCase() === "tesvolt admin"
    );
  }

  private getBatteryPassNavigationItem(): FuseNavigationItem {
    return {
      id: "battery-pass",
      title: "batteryPass.navigation",
      type: "basic",
      icon: "heroicons_outline:document-text",
      link: "/batterypass",
    };
  }

  private getCurrentUserPortalRole(): string | null {
    const parseValue = (value: string | null): any | null => {
      if (!value) return null;
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    };

    const loggedInUser = parseValue(localStorage.getItem("loggedInUser"));
    const userInfo = parseValue(localStorage.getItem("userInfo"));

    const role = loggedInUser?.portalRole ?? userInfo?.portalRole ?? null;

    return role ? String(role) : null;
  }

  private pruneEmptyCollapsableGroups(
    items: FuseNavigationItem[],
  ): FuseNavigationItem[] {
    return items.reduce<FuseNavigationItem[]>((result, item) => {
      const children = item.children
        ? this.pruneEmptyCollapsableGroups(
            item.children as FuseNavigationItem[],
          )
        : item.children;

      const nextItem: FuseNavigationItem = {
        ...item,
        ...(children ? { children } : {}),
      };

      if (
        nextItem.type === "collapsable" &&
        (!nextItem.children || nextItem.children.length === 0)
      ) {
        return result;
      }

      result.push(nextItem);
      return result;
    }, []);
  }

  private setupTopNavScrollBehavior(): void {
    if (typeof window === "undefined") {
      return;
    }

    this.lastScrollY = window.scrollY;

    this._ngZone.runOutsideAngular(() => {
      this.onWindowScroll = () => {
        const currentY = window.scrollY;

        if (this.isDialogOpen) {
          this.lastScrollY = currentY;
          return;
        }

        const deltaY = currentY - this.lastScrollY;

        // Ignore sub-pixel noise
        if (Math.abs(deltaY) < this.topNavJitterPx) {
          this.lastScrollY = currentY;
          return;
        }

        // Spec §5: always visible near top
        if (currentY < this.topNavScrollThreshold) {
          this.setTopNavVisible(true);
        } else if (deltaY > 0) {
          // Scrolling down — hide unless hovered
          if (!this.isTopNavHovered) {
            this.setTopNavVisible(false);
          }
        } else {
          // Scrolling up — reveal
          this.setTopNavVisible(true);
        }

        this.lastScrollY = currentY;
      };

      window.addEventListener("scroll", this.onWindowScroll, { passive: true });
    });
  }

  private teardownTopNavScrollBehavior(): void {
    if (typeof window === "undefined" || !this.onWindowScroll) {
      return;
    }

    window.removeEventListener("scroll", this.onWindowScroll);
    this.onWindowScroll = undefined;
  }

  private setTopNavVisible(visible: boolean): void {
    if (this.isTopNavVisible === visible) {
      return;
    }

    if (visible) {
      this.clearTopNavHideTimer();
    }

    this._ngZone.run(() => {
      this.isTopNavVisible = visible;
    });
  }

  private scheduleTopNavHide(): void {
    if (typeof window === "undefined") {
      return;
    }

    if (this.isDialogOpen) {
      return;
    }

    if (this.isTopNavHovered || window.scrollY < this.topNavScrollThreshold) {
      return;
    }

    this.clearTopNavHideTimer();
    this.hideTopNavTimer = setTimeout(() => {
      if (
        !this.isTopNavHovered &&
        window.scrollY >= this.topNavScrollThreshold
      ) {
        this.setTopNavVisible(false);
      }
    }, this.topNavHideDelayMs);
  }

  private clearTopNavHideTimer(): void {
    if (!this.hideTopNavTimer) {
      return;
    }

    clearTimeout(this.hideTopNavTimer);
    this.hideTopNavTimer = null;
  }
}
